import { computed, ref } from 'vue'
import type {
  AgentChatAction,
  AgentChatResponse,
  AgentModelStatus,
  ApiClient
} from '@xjlv2/api-client'
import { SYSTEM_PERMISSIONS } from '@xjlv2/api-client'
import type { PermissionManagementSection, ShellNotificationTarget, WorkspaceTab } from '../types'

export const SAAS_AGENT_EXAMPLES = [
  '新建角色 code guoxue_operator 名称 国学运营 guoxue 菜单',
  '创建用户 username zhangsan 姓名 张三 部门ID 1 角色ID 1',
  '打开国学AI咨询',
  '打开直播连麦',
  '打开用户记忆'
]

export interface UseSaasAgentOptions {
  createApiClient: () => ApiClient
  hasPermission: (permission: string) => boolean
  getContext: () => Record<string, unknown>
  onOpenTarget: (target: ShellNotificationTarget) => void
  onRefreshView: () => Promise<void> | void
  onError: (message: string) => void
  onNotice: (message: string) => void
}

export function useSaasAgent(options: UseSaasAgentOptions) {
  const modelStatus = ref<AgentModelStatus | null>(null)

  const modeLabel = computed(() => {
    const status = modelStatus.value
    if (!status) return ''
    return status.enabled ? `模型模式 · ${status.model || status.provider}` : '规则模式'
  })

  async function loadModelStatus() {
    if (!options.hasPermission(SYSTEM_PERMISSIONS.agentChat)) {
      modelStatus.value = null
      return
    }
    try {
      modelStatus.value = await options.createApiClient().getSaasAgentModelStatus()
    } catch {
      modelStatus.value = null
    }
  }

  async function sendChat(message: string, context: Record<string, unknown>) {
    return options.createApiClient().chatSaasAgent({ message, context })
  }

  async function handleResponse(response: AgentChatResponse) {
    options.onNotice(response.answer)
    const target = resolveAgentTarget(response)
    if (target) {
      options.onOpenTarget(target)
    }
    if (response.status === 'DONE') {
      await options.onRefreshView()
    }
  }

  function handleAction(action: AgentChatAction) {
    const target = normalizeAgentActionTarget(action)
    if (target) {
      options.onOpenTarget(target)
    }
  }

  function resolveAgentTarget(response: AgentChatResponse): ShellNotificationTarget | null {
    const responseTarget = normalizeAgentTarget(response.data)
    const actionTarget = response.actions
      .map((action) => normalizeAgentActionTarget(action))
      .find((target): target is ShellNotificationTarget => target !== null)
    return responseTarget ?? actionTarget ?? null
  }

  function normalizeAgentActionTarget(action: AgentChatAction): ShellNotificationTarget | null {
    return normalizeAgentTarget(action.result) ?? normalizeAgentTarget(action.arguments)
  }

  function normalizeAgentTarget(value: unknown): ShellNotificationTarget | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null
    const target = value as Record<string, unknown>
    const nestedTarget = normalizeAgentTarget(target.targetMenu) ?? normalizeAgentTarget(target.target)
    if (nestedTarget) return nestedTarget
    const moduleCode = typeof target.moduleCode === 'string' ? target.moduleCode : undefined
    const menuCode = typeof target.menuCode === 'string' ? target.menuCode : undefined
    const path = typeof target.path === 'string' ? target.path : undefined
    const tab = typeof target.tab === 'string' ? (target.tab as WorkspaceTab) : undefined
    const permissionSection =
      typeof target.permissionSection === 'string'
        ? (target.permissionSection as PermissionManagementSection)
        : undefined
    const view = typeof target.view === 'string' ? target.view : undefined
    const paymentModule = typeof target.paymentModule === 'string' ? target.paymentModule : undefined
    const mchOrderNo = typeof target.mchOrderNo === 'string' ? target.mchOrderNo : undefined
    const planId = typeof target.planId === 'string' ? target.planId : undefined
    const returnMenuCode = typeof target.returnMenuCode === 'string' ? target.returnMenuCode : undefined
    if (!moduleCode && !menuCode && !path && !tab && !permissionSection
      && !view && !paymentModule && !mchOrderNo && !planId && !returnMenuCode) return null
    return { moduleCode, menuCode, path, tab, permissionSection, view, paymentModule, mchOrderNo, planId, returnMenuCode }
  }

  function reset() {
    modelStatus.value = null
  }

  return {
    modelStatus,
    modeLabel,
    examples: SAAS_AGENT_EXAMPLES,
    loadModelStatus,
    sendChat,
    handleResponse,
    handleAction,
    reset
  }
}
